import type { Endpoints } from '../../../directus/extensions/gogigi/src/promoter-auth-endpoint/types.ts';
import { env } from '../env.ts';
import { getAuthHeaders } from './directusClient/directusClient.ts';

const baseUrl = `${env.VITE_DIRECTUS_API_URL}/promoter-auth-endpoint`;

/**
 * The endpoint extension declares its response shapes in `Endpoints`; this trusts the server to
 * honour them rather than validating every body at runtime.
 */
const readJson = async <T>(response: Response): Promise<T> => (await response.json()) as T;

const apiRequest = async <T>(endpoint: string, params: Record<string, string | number>): Promise<T> => {
  const response = await fetch(`${baseUrl}/${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify(params),
  });

  return readJson<T>(response);
};

export const signup = async (
  params: Endpoints['POST /signup']['Request'],
): Promise<Endpoints['POST /signup']['Response']> => apiRequest('signup', params);

/** Links a promoter to the account that just signed in with Google or Facebook. */
export const signupSocial = async (): Promise<Endpoints['POST /signup-social']['Response']> => {
  const response = await fetch(`${baseUrl}/signup-social`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
    body: '{}',
  });

  return readJson(response);
};

/** Verifies the emailed code; on success the server also logs the promoter in (sets the refresh cookie). */
export const verifyAccount = async (
  params: Endpoints['POST /verify-account']['Request'],
): Promise<Endpoints['POST /verify-account']['Response']> => {
  const response = await fetch(`${baseUrl}/verify-account`, {
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(params),
  });

  return readJson(response);
};

export const resendVerificationCode = async (
  params: Endpoints['POST /resend-verification-code']['Request'],
): Promise<Endpoints['POST /resend-verification-code']['Response']> => apiRequest('resend-verification-code', params);

const reset = async (
  params: Endpoints['POST /reset-password/reset']['Request'],
): Promise<Endpoints['POST /reset-password/reset']['Response']> => apiRequest('reset-password/reset', params);

const check = async (
  params: Endpoints['POST /reset-password/check']['Request'],
): Promise<Endpoints['POST /reset-password/check']['Response']> => apiRequest('reset-password/check', params);

const newPassword = async (
  params: Endpoints['POST /reset-password/new-password']['Request'],
): Promise<Endpoints['POST /reset-password/new-password']['Response']> =>
  apiRequest('reset-password/new-password', params);

export const resetPassword = { reset, check, newPassword };
